import { HQ, leagueCompetition, sportsimPath } from "../sportConfig";
import ShareButton from "../ShareButton";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import "./TipPage.css";
import "../sports/football.css";
import { footballCalendarDate } from "../sports/footballDate";
import SimulationTable from "./SimulationTable";

async function request(path, options) {
  const response = await fetch(sportsimPath(path), { credentials: "same-origin", ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || "Något gick fel. Försök igen.");
  return body;
}

const dateLabel = (value) => value ? new Intl.DateTimeFormat("sv-SE", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(value)) : "Tid ej satt";

const fixtureDateLabel = match => HQ.sport === "football" && match.kickoff_known === false
  ? `${match.starts_at ? footballCalendarDate(match.date || match.starts_at) + " · " : ""}Tid ej fastställd`
  : dateLabel(match.starts_at);

export default function TipPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [leagues, setLeagues] = useState([]);
  const [matches, setMatches] = useState([]);
  const [saved, setSaved] = useState({});
  const [drafts, setDrafts] = useState({});
  const [account, setAccount] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const [sharedAuth, setSharedAuth] = useState(false);
  const [waitingForEmail, setWaitingForEmail] = useState(() => window.sessionStorage.getItem("hq-waiting-for-email") === "true");
  const [standings, setStandings] = useState([]);
  const [teamFilter, setTeamFilter] = useState("");
  const [roundFilter, setRoundFilter] = useState("all");
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const selected = params.get("serie") || "";
  const from = params.get("from") || "";
  const backToMatch = from.startsWith("/match?") && !from.startsWith("//");
  const backTarget = backToMatch ? from : "/";
  const league = leagues.find((item) => item.key === selected);
  const view = params.get("vy") === "simulera" ? "simulera" : "matcher";
  const competitionId = leagueCompetition(league);

  useEffect(() => {
    Promise.all([request("/simulations"), request("/account"), request("/auth/config")])
      .then(([items, user, auth]) => {
        setLeagues(items);
        setSharedAuth(auth.shared);
        setAccount(user.authenticated ? user.email || "Sportsim-konto" : null);
        if (user.authenticated) { setWaitingForEmail(false); window.sessionStorage.removeItem("hq-waiting-for-email"); }
        if (!params.get("serie") && items.length) setParams({ serie: items.find((item) => !item.closed)?.key || items[0].key }, { replace: true });
      })
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!selected) return;
    let active = true;
    setLoading(true);
    setError("");
    setDrafts({});
    setTeamFilter("");
    setRoundFilter("all");
    Promise.all([request(`/matches?simulation=${encodeURIComponent(selected)}`), account ? request(`/predictions?simulation=${encodeURIComponent(selected)}`) : Promise.resolve([])])
      .then(([fixtures, predictions]) => {
        if (!active) return;
        setMatches(fixtures);
        setSaved(Object.fromEntries(predictions.map((tip) => [tip.match_id, tip])));
        setLoading(false);
      })
      .catch((err) => { if (active) { setError(err.message); setLoading(false); } });
    return () => { active = false; };
  }, [selected, account]);

  useEffect(() => {
    if (!competitionId) { setStandings([]); return; }
    let active = true;
    fetch(`${HQ.dataApi}/standings?competition_id=${competitionId}`).then((response) => response.json()).then((data) => { if (active) setStandings(data.standings || []); }).catch(() => { if (active) setStandings([]); });
    return () => { active = false; };
  }, [competitionId]);

  useEffect(() => {
    if (!sharedAuth || !waitingForEmail || account) return undefined;
    let active = true;
    const check = async () => {
      try {
        const status = await request("/auth/status");
        if (!active) return;
        if (status.authenticated) {
          setAccount("Sportsim-konto");
          setWaitingForEmail(false);
          window.sessionStorage.removeItem("hq-waiting-for-email");
          setNotice("Du är inloggad. Dina Sportsim-tips hämtas nu.");
        } else if (!status.pending) {
          setWaitingForEmail(false);
          window.sessionStorage.removeItem("hq-waiting-for-email");
          setNotice("Länken har gått ut. Du kan skicka en ny.");
        }
      } catch (err) { /* A temporary network error should not end the login attempt. */ }
    };
    check();
    const timer = setInterval(check, 4000);
    return () => { active = false; clearInterval(timer); };
  }, [sharedAuth, waitingForEmail, account]);

  const future = useMemo(() => matches.filter((match) => match.can_tip), [matches]);
  const teams = useMemo(() => [...new Set(matches.flatMap((match) => [match.home_team, match.away_team]))].sort((a, b) => a.localeCompare(b, "sv")), [matches]);
  const teamMatches = teamFilter ? future.filter((match) => match.home_team === teamFilter || match.away_team === teamFilter) : future;
  const rounds = [...new Set(teamMatches.map((match) => match.round_no).filter(Boolean))].sort((a, b) => a - b);
  const nextRound = [...teamMatches].sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""))[0]?.round_no;
  const selectedRound = roundFilter === "next" ? nextRound : Number(roundFilter);
  const visibleFuture = roundFilter === "all" ? teamMatches : teamMatches.filter((match) => match.round_no === selectedRound);
  const historyMatches = matches.filter((match) => !match.can_tip && (!teamFilter || match.home_team === teamFilter || match.away_team === teamFilter) && (roundFilter === "all" || match.round_no === selectedRound));
  const predictionMap = Object.fromEntries(matches.map((match) => { const draft = drafts[match.id]; return [match.id, draft ? { pred_home: draft.home, pred_away: draft.away, pred_result_type: draft.type } : saved[match.id]]; }));
  const hideFootballResults = HQ.sport === "football" && localStorage.getItem(`${HQ.storagePrefix}-hide-results`) === "true";
  const earlier = matches.length - future.length;
  const editable = future.filter((match) => !league?.closed);
  const count = editable.filter((match) => saved[match.id] || (drafts[match.id]?.home !== "" && drafts[match.id]?.home != null && drafts[match.id]?.away !== "" && drafts[match.id]?.away != null)).length;

  const change = (id, side, value) => {
    setNotice("");
    setDrafts((current) => ({ ...current, [id]: {
      home: current[id]?.home ?? (saved[id]?.pred_home != null ? String(saved[id].pred_home) : ""),
      away: current[id]?.away ?? (saved[id]?.pred_away != null ? String(saved[id].pred_away) : ""),
      type: current[id]?.type ?? saved[id]?.pred_result_type ?? "",
      [side]: value,
    } }));
  };

  const save = async () => {
    if (!account) { setShowAccount(true); document.getElementById("tip-account")?.scrollIntoView({ behavior: "smooth" }); return; }
    const predictions = Object.entries(drafts).filter(([, tip]) => tip.home !== "" && tip.away !== "")
      .map(([id, tip]) => ({ match_id: Number(id), pred_home: Number(tip.home), pred_away: Number(tip.away), pred_result_type: tip.type || null }));
    if (!predictions.length) { setNotice("Fyll i båda resultaten för minst en match."); return; }
    setBusy(true); setError("");
    try {
      await request("/predictions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ simulation: selected, predictions }) });
      const rows = await request(`/predictions?simulation=${encodeURIComponent(selected)}`);
      setSaved(Object.fromEntries(rows.map((tip) => [tip.match_id, tip])));
      setDrafts({});
      setNotice(`${predictions.length} ${predictions.length === 1 ? "tips sparat" : "tips sparade"}.`);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const submitAccount = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      if (sharedAuth) {
        await request("/auth/start", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: form.email, simulation: selected }) });
        setNotice("Kolla din e-post. Appen loggar in automatiskt när du bekräftat länken.");
        setWaitingForEmail(true);
        window.sessionStorage.setItem("hq-waiting-for-email", "true");
        setShowAccount(false);
        return;
      }
      const user = await request(`/${mode}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      setAccount(user.email); setShowAccount(false); setForm({ email: "", password: "" }); setNotice("Du är inloggad. Dina tips kan sparas nu.");
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const logout = async () => {
    await request("/logout", { method: "POST" });
    setAccount(null); setSaved({}); setDrafts({}); setWaitingForEmail(false); window.sessionStorage.removeItem("hq-waiting-for-email"); setNotice("Du är utloggad.");
  };

  const autofill = () => {
    const teamKey = (value) => String(value || "").toLocaleLowerCase("sv-SE").trim();
    if (!standings.length) { setNotice("Tabellen är inte tillgänglig ännu."); return; }
    const ranks = new Map(standings.map((row, index) => [teamKey(row.team), index]));
    const next = { ...drafts };
    let filled = 0;
    future.forEach((match) => {
      const existing = next[match.id] || saved[match.id];
      if (existing && (existing.home ?? existing.pred_home) !== "" && (existing.home ?? existing.pred_home) != null && (existing.away ?? existing.pred_away) !== "" && (existing.away ?? existing.pred_away) != null) return;
      if (!ranks.has(teamKey(match.home_team)) && !ranks.has(teamKey(match.away_team))) return;
      const homeBetter = (ranks.get(teamKey(match.home_team)) ?? Infinity) <= (ranks.get(teamKey(match.away_team)) ?? Infinity);
      next[match.id] = { home: homeBetter ? "1" : "0", away: homeBetter ? "0" : "1", type: "" };
      filled += 1;
    });
    setDrafts(next);
    setNotice(filled ? `${filled} matcher fylldes i från tabellen. Granska och spara när du är nöjd.` : "Alla möjliga matcher har redan tips.");
  };

  return <main className="tips-page">
    <header className="tips-header">
      <div className="tips-navigation hq-page-actions"><Link to={backTarget} replace className="tips-back">← {backToMatch ? "Matchcenter" : "Matcher"}</Link>{backToMatch && <Link to="/" className="tips-home-link">Matchlistan</Link>}<ShareButton path={`/tips?serie=${encodeURIComponent(selected)}`} title={league?.name ? `Tippa ${league.name}` : "Tippa & simulera"} /></div>
      <div className="tips-header-main"><div><span className="tips-kicker">{HQ.brand.toUpperCase()} · SPORTSIM</span><h1>Tippa & simulera</h1></div><button className="tips-account-trigger" onClick={() => setShowAccount(!showAccount)}>{account ? "Mitt konto" : "Logga in"}</button></div>
    </header>

    <section className="tips-panel tips-intro">
      <div><label htmlFor="tip-league">Serie</label><select id="tip-league" value={selected} onChange={(event) => setParams({ serie: event.target.value, ...(backToMatch ? { from } : {}) })}>
        {leagues.map((item) => <option value={item.key} key={item.key}>{item.name}{item.closed ? " · avslutad" : ""}</option>)}
      </select></div>
      <div className="tips-overview"><strong>{future.length}</strong><span>kommande matcher</span></div>
      <div className="tips-overview"><strong>{count}</strong><span>tippade eller påbörjade</span></div>
    </section>

    {error && <p className="tips-alert" role="alert">{error}</p>}
    {notice && <p className="tips-notice" role="status">{notice}</p>}

    {showAccount && <section id="tip-account" className="tips-panel tips-account tips-account-compact">
      {account ? <><div><h2>Mitt konto</h2><p>{account}</p></div><button className="tips-account-logout" onClick={logout}>Logga ut</button></> : <><div><h2>{sharedAuth ? "Logga in med e-post" : mode === "login" ? "Logga in" : "Skapa konto"}</h2><p>{sharedAuth ? "Du får en engångslänk från Sportsim. Samma konto fungerar där och här." : "Logga in när du vill spara tips. Resten av HQ fungerar utan konto."}</p></div>
      <form onSubmit={submitAccount}>
        {!sharedAuth && <div className="tips-mode"><button type="button" className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>Logga in</button><button type="button" className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>Skapa konto</button></div>}
        <label>E-post<input type="email" required autoComplete="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
        {!sharedAuth && <label>Lösenord<input type="password" required minLength={12} autoComplete={mode === "register" ? "new-password" : "current-password"} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>}
        {!sharedAuth && mode === "register" && <small>Minst 12 tecken.</small>}
        <button className="tips-primary" disabled={busy}>{sharedAuth ? "Skicka engångslänk" : mode === "login" ? "Logga in" : "Skapa konto"}</button>
      </form></>}
    </section>}

    {loading ? <div className="loading-container"><div className="loader" /><p className="loading-text">Hämtar matcher…</p></div> : <>
      <div className="tips-view-tabs" role="tablist" aria-label="Tipsvyer"><button role="tab" aria-selected={view === "matcher"} className={view === "matcher" ? "active" : ""} onClick={() => setParams({ serie: selected, ...(backToMatch ? { from } : {}) })}>Matcher</button><button role="tab" aria-selected={view === "simulera"} className={view === "simulera" ? "active" : ""} onClick={() => setParams({ serie: selected, vy: "simulera", ...(backToMatch ? { from } : {}) })}>Simulera tabell</button></div>
      <div className="tips-section-title"><div><span className="tips-kicker">{league?.season || ""}</span><h2>{league?.name || "Matcher"}</h2></div><span>{future.length} kvar · {earlier} tidigare</span></div>
      {league?.closed && <p className="tips-alert">Den här serien är avslutad. Matcherna visas för överblick.</p>}
      {view === "simulera" && <><p className="tips-simulation-help">Tabellen utgår från HQ:s aktuella tabell. Tips du fyller i räknas in direkt, utan att sparas automatiskt.</p><SimulationTable dividerRanks={league?.divider_ranks} standings={standings} matches={matches} predictions={predictionMap} rules={HQ.sport === "football" ? {...league?.point_rules, goalsForTiebreak:true} : league?.point_rules || {}} /></>}
      <div className="tips-tools"><div className="tips-filters"><label>Omgång <select value={roundFilter} onChange={(event) => setRoundFilter(event.target.value)}><option value="all">Alla omgångar</option><option value="next">Nästa omgång</option>{rounds.map((round) => <option value={String(round)} key={round}>{round}</option>)}</select></label><label>Lag <select value={teamFilter} onChange={(event) => { setTeamFilter(event.target.value); setRoundFilter("next"); }}><option value="">Alla lag</option>{teams.map((team) => <option key={team}>{team}</option>)}</select></label></div><button onClick={autofill} disabled={!standings.length || league?.closed}>Autofyll från tabell</button></div>
      {!visibleFuture.length && <p className="tips-empty">Inga kommande matcher finns för det här valet.</p>}
      <div className="tips-list">{visibleFuture.map((match) => {
        const draft = drafts[match.id]; const stored = saved[match.id];
        return <article className="tips-match" key={match.id}>
          <div className="tips-match-meta"><span>Omgång {match.round_no || "–"} · {fixtureDateLabel(match)}</span>{stored && !draft && <span className="tips-saved">✓ Sparat</span>}</div>
          <div className="tips-match-main"><span className="tips-team">{match.home_team}</span><div className="tips-score"><input aria-label={`${match.home_team} mål mot ${match.away_team}`} type="number" min="0" max="99" inputMode="numeric" placeholder="–" disabled={league?.closed} value={draft?.home ?? stored?.pred_home ?? ""} onChange={(event) => change(match.id, "home", event.target.value)} /><span>:</span><input aria-label={`${match.away_team} mål mot ${match.home_team}`} type="number" min="0" max="99" inputMode="numeric" placeholder="–" disabled={league?.closed} value={draft?.away ?? stored?.pred_away ?? ""} onChange={(event) => change(match.id, "away", event.target.value)} /></div><span className="tips-team tips-team-away">{match.away_team}</span></div>
          <div className="tips-match-footer"><Link to={`/match?url=${encodeURIComponent(match.match_link + "/laguppstallning")}&returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`}>Match & tabell ↗</Link>{HQ.sport !== "football" && <select aria-label={`Avgörande för ${match.home_team} mot ${match.away_team}`} disabled={league?.closed} value={draft?.type ?? stored?.pred_result_type ?? ""} onChange={(event) => change(match.id, "type", event.target.value)}><option value="">Ordinarie tid</option><option value="ef">Förlängning</option><option value="es">Straffar</option></select>}</div>
        </article>;
      })}</div>
      {historyMatches.length > 0 && <section className="tips-history"><h3>Startade och spelade matcher ({historyMatches.length})</h3><div>{historyMatches.map((match) => <div className="tips-history-row" key={match.id}><span>{fixtureDateLabel(match)}</span><strong>{match.home_team} {hideFootballResults ? "Resultat dolt" : `${match.home_goals ?? "–"}–${match.away_goals ?? "–"}`} {match.away_team}</strong><small>{match.home_goals == null ? "Startad" : saved[match.id] ? `Ditt tips: ${saved[match.id].pred_home}–${saved[match.id].pred_away}` : "Spelad"}</small></div>)}</div></section>}
      <p className="tips-external">Fler ligor? <a href={`/sportsim/?simulation=${encodeURIComponent(selected)}`} target="_blank" rel="noopener noreferrer">Öppna Sportsim ↗</a></p>
      {editable.length > 0 && <div className="tips-save-bar"><span>{Object.keys(drafts).length ? `${Object.keys(drafts).length} ändrade matcher` : "Fyll i resultat för att börja tippa"}</span><button className="tips-primary" disabled={busy || !Object.keys(drafts).length} onClick={save}>{busy ? "Sparar…" : account ? "Spara tips" : "Logga in och spara"}</button></div>}
    </>}
  </main>;
}
